import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { mapFamily } from '@/features/families/mappers';
import type { PatchFamilyBody } from '@/features/families/api';
import type { FamilyViewModel, PhysicalChartViewModel } from '@/models/family';
import { queryKeys } from '@/query/keys';

interface PatchContext {
  readonly previous: FamilyViewModel | undefined;
}

export interface FamilyDetailMutations {
  readonly patch: UseMutationResult<FamilyViewModel, Error, PatchFamilyBody, PatchContext>;
}

/**
 * Mutations against the family detail record (PATCH /families/{guid}).
 * Optimistic update on the merged view-model so the inline editors feel
 * snappy; rollback on error reinstates the prior snapshot, and onSettled
 * forces a refetch to pick up the server-bumped `updated_at`.
 */
export function useFamilyDetailMutations(guid: string): FamilyDetailMutations {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.family(guid);

  const patch = useMutation<FamilyViewModel, Error, PatchFamilyBody, PatchContext>({
    meta: { successMessage: 'success.toast.title.family_updated' },
    mutationFn: async (body) => {
      const dto = await apiClient.updateFamily({ guid, body });
      return mapFamily(dto);
    },
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<FamilyViewModel>(queryKey);
      if (previous !== undefined) {
        queryClient.setQueryData<FamilyViewModel>(queryKey, applyOptimisticPatch(previous, body));
      }
      return { previous };
    },
    onError: (_error, _body, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
      // name / customLabel show in the Browse Families list, so refresh it too.
      queryClient.invalidateQueries({ queryKey: ['recent-families'] }).catch(() => undefined);
    },
  });

  return { patch };
}

function applyOptimisticPatch(prev: FamilyViewModel, body: PatchFamilyBody): FamilyViewModel {
  let next: FamilyViewModel = prev;
  if (body.name !== undefined && body.name.length > 0) {
    next = { ...next, name: body.name };
  }
  if ('custom_label' in body) {
    next = { ...next, customLabel: normalizeBlank(body.custom_label) };
  }
  if ('notes' in body) {
    next = { ...next, notes: normalizeBlank(body.notes) };
  }
  if ('physical_chart' in body) {
    next = { ...next, physicalChart: normalizePhysicalChart(body.physical_chart) };
  }
  return next;
}

function normalizeBlank(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  return value.length === 0 ? null : value;
}

function normalizePhysicalChart(
  value: PatchFamilyBody['physical_chart'],
): PhysicalChartViewModel | null {
  if (value === null || value === undefined) return null;
  const id = value.id ?? null;
  const date = normalizeBlank(value.date);
  const location = normalizeBlank(value.location);
  const staffMemberUserGuid = normalizeBlank(value.staff_member_user_guid);
  const notes = normalizeBlank(value.notes);
  if (date === null && location === null && staffMemberUserGuid === null && notes === null) {
    return null;
  }
  return { id, date, location, staffMemberUserGuid, notes };
}
